import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'default';
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx.confirm;
}

/** `confirm` when a ConfirmProvider is mounted, else undefined (for shells like `Modal` that also render outside the app). */
export function useOptionalConfirm() {
  return useContext(ConfirmContext)?.confirm;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setState({ options, resolve });
    });
  }, []);

  const handleClose = (result: boolean) => {
    state?.resolve(result);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(false); }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-title"
        >
          <div
            className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl dark:bg-gray-800"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 rounded-full p-2 ${
                state.options.variant === 'danger' ? 'bg-red-100 dark:bg-red-900/40' :
                state.options.variant === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/40' : 'bg-blue-100 dark:bg-blue-900/40'
              }`}>
                <AlertTriangle className={`h-5 w-5 ${
                  state.options.variant === 'danger' ? 'text-red-600 dark:text-red-300' :
                  state.options.variant === 'warning' ? 'text-yellow-600 dark:text-yellow-300' : 'text-blue-600 dark:text-blue-300'
                }`} />
              </div>
              <div className="flex-1">
                <h3 id="confirm-dialog-title" className="text-sm font-semibold text-gray-900 dark:text-white">
                  {state.options.title || 'Confirm'}
                </h3>
                <p className="mt-1 whitespace-pre-line text-sm text-gray-600 dark:text-gray-300">{state.options.message}</p>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => handleClose(false)}
                className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                {state.options.cancelLabel || 'Cancel'}
              </button>
              <button
                type="button"
                onClick={() => handleClose(true)}
                autoFocus
                className={`rounded-lg px-4 py-2.5 text-sm font-medium text-white ${
                  state.options.variant === 'danger'
                    ? 'bg-red-600 hover:bg-red-700'
                    : state.options.variant === 'warning'
                      ? 'bg-yellow-600 hover:bg-yellow-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {state.options.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
